'use client';

import { api } from '@/api';
import { ScrollShadow, cn } from '@heroui/react';
import { useEffect, useRef, useState } from 'react';
import MessageCard from './message-card';
import PromptInputWithBottomActions from './prompt-input-with-bottom-actions';

export type ConversationMessage = {
  role: 'user' | 'system';
  message: string;
};

export const Conversation = ({
  chatId,
  messagesHistory,
}: {
  chatId: string;
  messagesHistory: ConversationMessage[];
}) => {
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const [messages, setMessages] = useState<ConversationMessage[]>(messagesHistory || []);
  const lastMessage = messages.at(-1);
  const isAnswerReceived =
    !!chatId && (lastMessage ? lastMessage.role === 'system' && lastMessage.message !== '...' : true);

  useEffect(() => {
    setMessages(messagesHistory);
  }, [messagesHistory]);

  const onMessageAdd = async (newMessage: string) => {
    if (!chatId) return;

    setMessages([...messages, { role: 'user', message: newMessage }]);
    setMessages((prevMessages) => [...prevMessages, { role: 'system', message: '...' }]);
    lastMessageRef.current?.scrollIntoView();
    try {
      const response: { response: string } = await api.post('/api/message', {
        chatId,
        text: newMessage,
      });
      setMessages((prevMessages) => {
        prevMessages.splice(-1);
        return [...prevMessages, { role: 'system', message: response.response }];
      });
    } catch {
      console.log('Failed');
    }
  };

  useEffect(() => {
    lastMessageRef.current?.scrollIntoView();
  }, [messages]);

  return (
    <>
      {!!messages.length && (
        <ScrollShadow className={'flex flex-col pb-[40px]'}>
          <div className="flex flex-col gap-4 px-1">
            {messages.map(({ role, message }, index) => (
              <MessageCard
                key={index}
                ref={index === messages.length - 1 ? lastMessageRef : null}
                attempts={index === 1 ? 2 : 1}
                avatar={
                  role === 'system'
                    ? 'https://pbs.twimg.com/profile_images/1774497511987724289/qIqYZUu__400x400.jpg'
                    : undefined
                }
                currentAttempt={index === 1 ? 2 : 1}
                message={message === '...' && role === 'system' ? '' : message}
                messageClassName={role === 'user' ? 'bg-content3 text-content3-foreground' : ''}
                showFeedback={role === 'system'}
                isReversed={role === 'user'}
                status={message === '...' && role === 'system' ? 'loading' : 'success'}
                isLast={index === messages.length - 1}
              />
            ))}
          </div>
        </ScrollShadow>
      )}
      <div className={cn('flex flex-col gap-2', !!messages.length && 'mt-auto')}>
        <PromptInputWithBottomActions onSubmit={onMessageAdd} isEnabled={isAnswerReceived} />
        <p className="px-2 text-tiny text-default-400">
          Q AI can make mistakes. Consider checking important information.
        </p>
      </div>
    </>
  );
};

export default Conversation;
